import {Component} from 'react';
import Keyword from './Keyword.js';
import './KeywordList.scss';
import languages from '../languages.js';
import LanguageInfo from './LanguageInfo.js';
import CheckListError from './CheckListError.js';
import Check from './Check.js';
import TextUtils from '../lib/TextUtils.js';

class KeywordList extends Component {

	keywordCheck = {
		id: 'related-keywords-in-text',
		mounted: true
	};

	constructor(props) {
		super(props);

		this.state = {
			newKeyword: '',
			language: languages.find((lang) => lang.langCode === this.props.language),
			location: this.props.location,
			error: '',
		}

		this.removeKeyword = this.removeKeyword.bind(this);
		this.removeKeywords = this.removeKeywords.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.calculateKeywordCoverage = this.calculateKeywordCoverage.bind(this);
		this.checkOptimalKeywordCoverage = this.checkOptimalKeywordCoverage.bind(this);
		this.handleButtonClick = this.handleButtonClick.bind(this);
	}

	removeKeyword(event) {
		event.preventDefault();
		const button = event.target.closest('.keyword-remove');
		let newKeywords = this.props.relatedKeywords.filter((keyword) => keyword.name !== button.dataset.word);
		this.props.saveRelatedKeywords(newKeywords);
	}

	removeKeywords() {
		if (!this.props.relatedKeywords.length) {
			return true;
		}
		if (window.confirm(window?.t?.REMOVE_RELATED_KEYWORDS_CONFIRM)) {
			this.props.saveRelatedKeywords([]);
			return true;
		}
		return false;
	}

	componentDidUpdate() {
		if (this.props.language !== this.state.language.langCode) {
			this.setState({
				language: languages.find((lang) => lang.langCode === this.props.language),
			});
		}
		if (this.props.location !== this.state.location) {
			this.setState({
				location: this.props.location,
			});
		}
	}

	async handleButtonClick(event) {
		event.preventDefault();
		this.setState({
			error: '',
		});
		if (!this.props.pageContent.keyword.trim()) {
			this.setState({
				loading: false,
				error: window?.t?.ERR_NO_FOCUS_KEYWORD,
			});
			return;
		}
		const canProceed = await this.props.limitUsageCallback('content-related-keywords', 'content-related-keywords');
		if (!canProceed) {
			this.setState({
				loading: false,
				error: window?.t?.ERR_LIMIT_REACHED_NO_MORE_CREDITS,
			});
			return;
		}

		if (this.removeKeywords()) {
			await this.props.getOptimalValues();
		}
	}

	handleKeyDown(event) {
		if (event.keyCode === 13 && this.state.newKeyword) {
			let newKeywords = this.state.newKeyword.split(',');
			newKeywords = newKeywords.map((keyword) => {
				return {
					name: keyword.trim(),
					repeat_min: null,
					repeat_max: null,
				}
			});

			let keywords = this.props.relatedKeywords;
			if (newKeywords.length) {
				newKeywords.forEach((newKeyword) => {
					let ok = true;
					if (keywords.length) {
						keywords.forEach((keyword) => {
							if (keyword.name === newKeyword.name) {
								ok = false;
							}
						});
					}

					if (ok) {
						keywords.push(newKeyword);
					}
				});
			}

			this.props.saveRelatedKeywords(keywords);

			this.setState({
				newKeyword: '',
			})
		}
	}

	handleChange(event) {
		this.setState({
			newKeyword: event.target.value
		})
	}

	checkKeyword(keyword) {
		let found = TextUtils.countOccurrences(keyword.name, this.props.pageContent.content);

		return {
			found: found,
			status: found ? 'ok' : 'nok',
		}
	}

	render() {
		let listItems = [];
		let removeKeywordsButton = null;
		const {pageContent, relatedKeywords} = this.props;

		if (relatedKeywords) {
			listItems = relatedKeywords.map((keyword, index) => {
				if (!keyword) {
					return null;
				}
				const keywordState = this.checkKeyword(keyword);
				return (
					<Keyword
						word={keyword.name}
						key={index}
						minCount={keyword.repeat_min}
						maxCount={keyword.repeat_max}
						removeCallback={this.removeKeyword}
						found={keywordState.found}
						status={keywordState.status}
					/>
				)
			});

			removeKeywordsButton = (<p className="main-button"><button className="button secondary" onClick={this.removeKeywords}>{window?.t?.REMOVE_RELATED_KEYWORDS}</button></p>)
		}

		const keywordLoaderButton = pageContent.keyword ? (
			<button className="button primary" onClick={this.handleButtonClick}>{window?.t?.GET_RELATED_KEYWORDS_SEO_DATA}</button>
			) : (
			<button className="button primary" disabled onClick={this.handleButtonClick}>{window?.t?.GET_RELATED_KEYWORDS_SEO_DATA}</button>
			);

		const error = this.state.error ? (<CheckListError text={this.state.error} />) : null;

		return (
			<div className="keyword-list">
				<h3>{window?.t?.RELATED_KEYWORDS}</h3>
				<LanguageInfo
				language={this.state.language}
				location={this.state.location}
				toggleSettingsCallback={this.props.toggleSettingsCallback}
				/>
				{error}
				<div className="keyword-loader" id="keyword-loader">
					<p className="main-button">{keywordLoaderButton}</p>
					{pageContent.keyword ? <p className="main-button"><button className="button secondary" onClick={this.props.optimalDataHandler}>{window?.t?.GET_SEO_DATA_ONLY}</button></p> : ''}
				</div>
				<p>
					<label htmlFor="new-keyword">{window?.t?.ADD_KEYWORDS_MANUALLY}</label>
					<input type="text" id="new-keyword" status="" onKeyDown={this.handleKeyDown} onChange={this.handleChange} value={this.state.newKeyword} placeholder={window?.t?.NEW_KEYWORD_PLACEHOLDER} />
				</p>

				<ul className="list">{listItems}</ul>
				{removeKeywordsButton}
				<Check
					id="keyword-coverage"
					label={window?.t?.RELATED_KEYWORD_COVERAGE}
					test={this.calculateKeywordCoverage}
					postData={this.props.pageContent}
					reportCallback={this.props.reportCallback}
				/>
				<Check
					id="optimal-keyword-coverage"
					label={window?.t?.OPTIMAL_RELATED_KEYWORD_COVERAGE}
					test={this.checkOptimalKeywordCoverage}
					postData={this.props.pageContent}
					reportCallback={this.props.reportCallback}
				/>
			</div>
		)
	}

	calculateKeywordCoverage() {
		const {relatedKeywords} = this.props;
		if (!relatedKeywords.length) {
			return {
				status: false,
				value: 0,
			}
		}

		let foundKeywords = 0;
		relatedKeywords.forEach((keyword) => {
			let result = this.checkKeyword(keyword);
			if (result.found) {
				foundKeywords ++;
			}
		});

		let keywordCoverage = foundKeywords / relatedKeywords.length * 100;
		return {
			status: !!keywordCoverage,
			value: `${keywordCoverage.toFixed(2)}%`,
		}
	}

	checkOptimalKeywordCoverage() {
		const {relatedKeywords, optimalData} = this.props;
		if (!relatedKeywords.length || !optimalData || (!optimalData.optimalKeywordCoverage && optimalData.optimalKeywordCoverage !== 0)) {
			return {
				status: false,
			}
		}

		let optimalKeywordCoverage = optimalData.optimalKeywordCoverage;
		let foundKeywords = 0;
		relatedKeywords.forEach((keyword) => {
			let result = this.checkKeyword(keyword);
			if (result.found) {
				foundKeywords ++;
			}
		});

		let keywordCoverage = foundKeywords / relatedKeywords.length * 100;
		keywordCoverage = parseFloat(keywordCoverage.toFixed(2));

		return {
			status: keywordCoverage >= optimalKeywordCoverage,
			optimalMin: optimalKeywordCoverage + '%',
		}

	}
}

export default KeywordList;